import {
  GetCommentsSuccessType,
  GetPostsSuccessType,
  NtfCommentsRequestType,
  UpdateCommentRequestType,
} from 'redux/actions/postActions';

import { updateCommentCounter, updateComments } from 'redux/utils/commentsUtils';
import { CommentType, CommentsMode, PostGetCommentPayload, PostType, PostUploadType } from 'types/postTypes';
import { ActionType, PayloadErrorType } from 'types/reduxTypes';
import {
  CHANGE_UPLOAD_POST_FILE,
  CLEAR_CURRENT_REPOST,
  CLEAR_EDITING_POST,
  CREATE_POST,
  CREATE_POST_FAILED,
  CREATE_POST_SUCCESS,
  DELETE_COMMENT,
  DELETE_COMMENT_FAILED,
  DELETE_COMMENT_SUCCESS,
  DELETE_POST,
  DELETE_POST_FAILED,
  DELETE_POST_SUCCESS,
  GET_COMMENTS,
  GET_COMMENTS_FAILED,
  GET_COMMENTS_SUCCESS,
  GET_POST,
  GET_POSTS,
  GET_POSTS_FAILED,
  GET_POSTS_SUCCESS,
  GET_POST_COMMENT_WS_SUCCESS,
  GET_POST_FAILED,
  GET_POST_SUCCESS,
  LIKE_COMMENT_SUCCESS,
  LINK_FILES,
  LINK_FILES_FAILED,
  LINK_FILES_SUCCESS,
  POST_COMMENT,
  POST_COMMENT_FAILED,
  POST_COMMENT_SUCCESS,
  SET_CURRENT_REPOST,
  SET_EDITING_POST,
  SET_NTF_STATUS_SUCCESS,
  UNLINK_FILES,
  UNLINK_FILES_FAILED,
  UNLINK_FILES_SUCCESS,
  UPDATE_COMMENT,
  UPDATE_COMMENT_FAILED,
  UPDATE_COMMENT_SUCCESS,
  UPDATE_COMMENT_WS,
  UPDATE_POST,
  UPDATE_POST_FAILED,
  UPDATE_POST_SUCCESS,
  UPLOAD_POST_FILE_FAILED,
  UPLOAD_POST_FILE_SUCCESS,
} from '../constants';

import { deleteUploadItem } from 'redux/utils/postUtils';
import { FileDescriptor } from 'types/fileTypes';
import { PostStateType } from 'types/stateTypes';
import { idOnly } from 'utils/cls.utils';

export type PostPayloadType =
  | PostType
  | PostUploadType
  | GetPostsSuccessType
  | PostGetCommentPayload
  | null
  | PayloadErrorType;

const initialState: PostStateType = {
  comments: [],
  currentPost: null,
  currentRepost: null,
  editingPost: null,
  error: null,
  hasMore: true,
  hasMoreComments: true,
  isCommentProcessing: false,
  isCommentsLoading: false,
  isLoading: false,
  mode: CommentsMode.inOrder,
  posts: [],
  uploadFiles: [],
};

export default (state = initialState, action: ActionType<PostPayloadType>): PostStateType => {
  switch (action.type) {
    case SET_EDITING_POST: {
      const post = action.payload as PostType;
      return { ...state, editingPost: post, uploadFiles: (post.files as FileDescriptor[]) || [] };
    }
    case CLEAR_EDITING_POST: {
      return { ...state, editingPost: null };
    }
    case CREATE_POST: {
      return { ...state, isLoading: true };
    }
    case CREATE_POST_SUCCESS: {
      return { ...state, isLoading: false };
    }
    case CREATE_POST_FAILED: {
      return { ...state, isLoading: false };
    }
    case UPDATE_POST: {
      return { ...state, isLoading: true };
    }
    case UPDATE_POST_SUCCESS: {
      const updatedPost = action.payload as PostType;
      const updatedPosts = state.posts.map((post) => (post.id !== updatedPost.id ? post : updatedPost));
      return { ...state, isLoading: false, posts: updatedPosts };
    }
    case UPDATE_POST_FAILED: {
      return { ...state, isLoading: false };
    }
    case DELETE_POST: {
      return { ...state, isLoading: true };
    }
    case DELETE_POST_SUCCESS: {
      const deletedPostId = action.payload as string;
      const updatedPosts = state.posts.filter((post) => post.id !== deletedPostId);
      return { ...state, isLoading: false, posts: updatedPosts };
    }
    case DELETE_POST_FAILED: {
      return { ...state, isLoading: false };
    }
    case GET_POST: {
      return { ...state, isLoading: true };
    }
    case GET_POST_SUCCESS: {
      const post = action.payload as PostType;
      return { ...state, currentPost: post, isLoading: false };
    }
    case GET_POST_FAILED: {
      return { ...state, isLoading: false };
    }
    case GET_POSTS: {
      return { ...state, isLoading: true };
    }

    case GET_POSTS_SUCCESS: {
      const { posts, isAppend, hasMore } = action.payload as GetPostsSuccessType;
      const newState = { ...state, hasMore, isLoading: false };
      if (isAppend) {
        return { ...newState, posts: [...state.posts, ...posts] };
      } else {
        return { ...newState, posts };
      }
    }

    case GET_POSTS_FAILED: {
      return { ...state, isLoading: false };
    }
    case LINK_FILES: {
      return { ...state };
    }
    case LINK_FILES_FAILED: {
      return { ...state };
    }
    case LINK_FILES_SUCCESS: {
      return { ...state };
    }
    case UNLINK_FILES: {
      return { ...state };
    }
    case UNLINK_FILES_FAILED: {
      return { ...state };
    }
    case UNLINK_FILES_SUCCESS: {
      return { ...state };
    }
    case SET_CURRENT_REPOST: {
      const { repost } = action.payload as { repost: PostType };
      return { ...state, currentRepost: repost };
    }
    case CLEAR_CURRENT_REPOST: {
      return { ...state, currentRepost: null };
    }
    case UPLOAD_POST_FILE_SUCCESS: {
      const { data } = action.payload as unknown as { data: FileDescriptor[] };
      const update = {} as { uploadFiles: FileDescriptor[] };
      if (data) update.uploadFiles = state.uploadFiles.concat(data);
      return { ...state, ...update };
    }
    case CHANGE_UPLOAD_POST_FILE: {
      const { fileId, isClear } = action.payload as { fileId: string; isClear: boolean };
      const { uploadFiles } = state;
      const updatedFiles = isClear ? [] : deleteUploadItem(fileId, uploadFiles);
      return { ...state, uploadFiles: updatedFiles };
    }
    case UPLOAD_POST_FILE_FAILED: {
      return { ...state };
    }
    case GET_COMMENTS: {
      return { ...state, isCommentsLoading: true };
    }
    case GET_COMMENTS_FAILED: {
      return { ...state, isCommentsLoading: false };
    }
    case GET_COMMENTS_SUCCESS: {
      const { commentId, comments, isAppend, hasMore, mode } = action.payload as GetCommentsSuccessType;
      const newState = { ...state, hasMoreComments: hasMore, isCommentsLoading: false, mode };
      const stateComments = [...state.comments];
      if (commentId) {
        const index = stateComments.findIndex((x) => x.id === commentId);
        if (index !== -1) {
          stateComments.splice(index + 1, 0, ...comments.slice(1));
        }
        return {
          ...state,
          childComments: {
            ...state.childComments,
            [commentId]: { count: comments.length, lastChild: comments[comments.length - 1] },
          },
          comments: stateComments,
          isCommentsLoading: false,
        };
      }
      if (isAppend) {
        return { ...newState, comments: [...stateComments, ...comments] };
      } else {
        return { ...newState, childComments: {}, comments };
      }
    }
    case POST_COMMENT: {
      return { ...state, isCommentProcessing: true };
    }
    case POST_COMMENT_FAILED: {
      return { ...state, error: action.payload, isCommentProcessing: false };
    }
    case POST_COMMENT_SUCCESS: {
      const comment = action.payload as CommentType;
      let newComments = [...state.comments];
      if (comment.up) {
        const addIndex = newComments.findIndex((x) => x.id === comment.up);
        newComments.splice(addIndex + 1, 0, comment);
      } else {
        newComments = [comment, ...state.comments];
      }
      return {
        ...state,
        comments: newComments,
        error: null,
        isCommentProcessing: false,
      };
    }

    case UPDATE_COMMENT: {
      return { ...state, isCommentProcessing: true };
    }
    case UPDATE_COMMENT_FAILED: {
      return { ...state, isCommentProcessing: false };
    }
    case UPDATE_COMMENT_SUCCESS: {
      return { ...state, isCommentProcessing: false };
    }

    case DELETE_COMMENT: {
      return { ...state, isCommentProcessing: true };
    }
    case DELETE_COMMENT_FAILED: {
      return { ...state, isCommentProcessing: false };
    }
    case DELETE_COMMENT_SUCCESS: {
      const deletedComment = action.payload as CommentType;
      const updatedComments = state.comments.filter((comment) => comment.id !== deletedComment.id);
      return { ...state, comments: updatedComments, isCommentProcessing: false };
    }

    case LIKE_COMMENT_SUCCESS: {
      const commentId = action.payload as string;
      const updatedComments = updateComments(state.comments, commentId);
      return { ...state, comments: updatedComments as CommentType[] };
    }

    case SET_NTF_STATUS_SUCCESS: {
      const { ntfStatus } = action.payload as NtfCommentsRequestType;
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const newCurrentPost = { ...state.currentPost!, commentNtfStatus: ntfStatus };
      return { ...state, currentPost: newCurrentPost };
    }

    case GET_POST_COMMENT_WS_SUCCESS: {
      const { rParObj: postId, rSrc: authorId, rObj: commentId, html } = action.payload as PostGetCommentPayload;

      return {
        ...state,
        newNotification: { authorId: idOnly(authorId), commentId: idOnly(commentId), html, postId: idOnly(postId) },
      };
    }

    case UPDATE_COMMENT_WS: {
      const { commentId, reaction_count } = action.payload as UpdateCommentRequestType;
      const updatedComments = updateCommentCounter(state.comments, commentId, reaction_count);
      return { ...state, comments: updatedComments as CommentType[] };
    }

    default: {
      return state;
    }
  }
};
